import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatRow, MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { TranslocoService, translate } from '@jsverse/transloco';
import {
  ReplaySubject,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  filter,
  skip,
} from 'rxjs';

import { DropdownObject } from '@inmoment/the-kitchen';

import { Account, SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import { OriginTypes } from 'app/providers/semantria-api/semantria-account.service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { Collection } from 'app/providers/storage-api/collection.service';
import { PredictiveService } from 'app/providers/storage-api/predictive.service';
import { Project } from 'app/providers/storage-api/project.service';
import { UploadState } from 'app/providers/storage-api/upload.service';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Utils } from 'app/utils/utils';

import { ItemLabel, ObjectLabel } from '../../../../providers/storage-api/object-label.service';
import { ProjectCollectionActionsComponent } from '../project-collection-actions/project-collection-actions.component';
import { ProjectDashboardActionsComponent } from '../project-dashboard-actions/project-dashboard-actions.component';
import { ProjectGroupActionsComponent } from '../project-group-actions/project-group-actions.component';
import {
  SpotlightDashboardIdeasDialogData,
  SpotlightDashboardIdeasDialogResult,
} from '../spotlight-dashboard-ideas-dialog/spotlight-dashboard-ideas-dialog.component';
import { ProjectAnalysisGroup } from '../../models/project-analysis-group.model';
import { ProjectAnalysis } from '../../models/project-analysis.model';
import { ProjectCollection } from '../../models/project-collection.model';
import { ProjectDashboard } from '../../models/project-dashboard.model';
import {
  AnalysisGroupCollectionEvent,
  ProjectDetailService,
  ProjectItem,
} from '../../services/project-detail.service';

enum TableType {
  all,
  dashboard,
  collection,
  group,
}

@EnableClassTelemetry()
@Component({
  selector: 'app-project-details-table',
  templateUrl: './project-details-table.component.html',
  styleUrls: ['./project-details-table.component.scss'],
  standalone: false,
})
export class ProjectDetailsTableComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() project: Project;
  @Input() isReadOnly: boolean;
  @Input() isExecutive: boolean;
  @Input() isAdmin: boolean;
  @Input() isDebug: boolean;

  @ViewChildren(MatRow, { read: ElementRef }) rows!: QueryList<ElementRef<HTMLTableRowElement>>;
  @ViewChild(ProjectDashboardActionsComponent) dashboardActions: ProjectDashboardActionsComponent;
  @ViewChild(ProjectGroupActionsComponent) groupActions: ProjectGroupActionsComponent;
  @ViewChild(ProjectCollectionActionsComponent)
  collectionActions: ProjectCollectionActionsComponent;
  @ViewChild('projectDetailsTable') projectDetailsTable: MatTable<ProjectItem>;

  projectItemsColumns = ['type', 'name', 'details', 'modified', 'actions'];
  labelsColumnSpan = 3;
  projectItemsDatasource: MatTableDataSource<ProjectItem>;
  tableItems: ProjectItem[] = [];
  selectedItem: ProjectItem;
  itemHasLabels = (idx, item: ProjectItem) => item.labels.length;
  search$ = new Subject<string>();
  private filterValue: string;
  selectedItemSub$: Subscription;
  private unsub$ = new ReplaySubject<void>(1);
  projectCollections: ProjectCollection[];
  configurations: Map<string, SemantriaConfiguration> = null;
  groupConfigurations: SemantriaConfiguration[];
  account: Account;
  isLoading: boolean;
  icons: any;
  subTableItem: ProjectItem;
  embeddingsEnabled = false;
  originTypes = OriginTypes;
  hasSpotlightAssistantFeature = false;
  showSpotlightIdeasDialog = false;
  spotlightIdeasDialogData: SpotlightDashboardIdeasDialogData;
  spotlightIdeasDialogMode: 'dashboard' | 'chat' = 'dashboard';
  vibeItUpDisabled = false;
  vibeItUpSubtitle: string | undefined;

  vibeItUpDropdown: DropdownObject[] = [
    {
      title: '',
      id: 'dashboard',
      visual: 'reporting_filled',
      color: 'CX',
    },
    {
      title: '',
      id: 'chat',
      visual: 'comment_filled',
      color: 'EX',
    },
  ];

  sort = {
    active: 'modified',
    direction: 'desc',
  };
  readonly sortTypeOrder = { dashboard: 0, collection: 1, group: 2 };
  protected readonly Math = Math;

  showImportDialog;
  importValidator;

  createItemDropdown: DropdownObject[] = [
    {
      title: '',
      id: 'dashboard',
      color: 'CX',
      visual: 'reporting_filled',
      disabled: false,
    },
    {
      title: '',
      id: 'collection',
      color: 'success',
      visual: 'document_multiple_filled',
      disabled: false,
    },
    {
      title: '',
      id: 'group',
      color: 'EX',
      visual: 'collections_filled',
      disabled: false,
    },
  ];

  tableTabs = [];
  activeTableTabIndex = 0;
  selectedItemProjectionId: string;
  projectionBreadcrumbs;

  constructor(
    private projectDetailService: ProjectDetailService,
    private accountService: SemantriaAccount,
    private predictiveService: PredictiveService,
    private analysisService: AnalysisService,
    private translocoService: TranslocoService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private logger: LoggerService
  ) {}

  ngOnInit() {
    this.translocoService
      .selectTranslateObject('project.project-details-table')
      .pipe(takeUntil(this.unsub$))
      .subscribe((t) => this.setPropTranslations(t));
    this.icons = this.projectDetailService.projectItemIcons;
    this.projectDetailService.changeSelectedItem(null);

    this.search$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.unsub$))
      .subscribe((val) => {
        this.filterValue = val;
        this.projectItemsDatasource.filter = val;
      });

    this.projectDetailService.currentItem
      .pipe(takeUntil(this.unsub$))
      .subscribe((item) => (this.selectedItem = item));

    this.projectDetailService.currentTab.pipe(takeUntil(this.unsub$)).subscribe((data) => {
      this.selectedItemProjectionId = data.projectionId;
      this.onTabChanged(data.tabId, data.itemId);
    });

    this.projectDetailService.currentSubTableItem.pipe(takeUntil(this.unsub$)).subscribe((item) => {
      this.onOpenItem(item, null);
    });

    this.projectDetailService.labelsExistenceChanged$.pipe(takeUntil(this.unsub$)).subscribe(() => {
      this.projectDetailsTable.renderRows();
    });

    // We need to filter out system labels so we are not creating duplicates upon getting in-out of a detail projection
    const mapItemLabelsToObjectLabels = (arr: Partial<ItemLabel>[]) => {
      return arr
        .filter((label) => label.type !== 'system')
        .map<ObjectLabel>((label) => {
          const { ...objectLabel } = label;
          return <ObjectLabel>objectLabel;
        });
    };

    this.projectDetailService.analysisLabelsChanged$
      .pipe(takeUntil(this.unsub$))
      .subscribe((analysis) => {
        const analyses =
          this.subTableItem.type === 'collection'
            ? this.subTableItem.collection.analyses
            : this.subTableItem.group.analyses;
        analyses.find((a) => a.name === analysis.analysisName).objectLabels =
          mapItemLabelsToObjectLabels(analysis.labels);
      });

    // We need to persist the labelChanges for collections as well
    this.projectDetailService.collectionLabelsChanged$
      .pipe(takeUntil(this.unsub$))
      .subscribe((collection) => {
        this.project.collections.find(
          (c) => c.collectionName === collection.collectionName
        ).objectLabels = mapItemLabelsToObjectLabels(collection.labels);
      });

    this.subscribeToAnalysisEvents();
    this.subscribeToDriverAnalysisEvents();
    this.subscribeToAnalysisGroupEvents();

    this.route.params
      .pipe(skip(1), takeUntil(this.unsub$))
      .subscribe((params) => this.syncStateFromRoute(params));

    this.accountService
      .hasAccountFeature('SPOTLIGHT_AI_ASSISTANT')
      .then((flag) => {
        this.hasSpotlightAssistantFeature = flag;
      })
      .catch(() => {
        this.hasSpotlightAssistantFeature = false;
      });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.project?.currentValue) {
      if (!this.configurations) {
        await this.init();
      }
      // wait for *Action child components ngOnChanges to update project
      setTimeout(() => {
        this.setProjectItemData();
      });
    }
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private subscribeToAnalysisEvents() {
    const handleActionOnCollection = (collectionName, callbackFn) => {
      [this.projectItemsDatasource.data, this.tableItems]
        .flatMap((items) =>
          items.filter((item) => item.type === 'collection' && item.id === collectionName)
        )
        .forEach(callbackFn);
    };

    const handleActionOnAnalysisGroupCollection = (event: AnalysisGroupCollectionEvent) => {
      [this.projectItemsDatasource.data, this.tableItems].forEach((items) => {
        items
          .filter((item) => item.type === 'group')
          .forEach((item) => {
            if (event.type === 'DELETE') {
              item.group.analyses = item.group.analyses.filter(
                (a) =>
                  a.collectionName !== event.data.collectionName &&
                  (!event.data.analysisName || a.name !== event.data.analysisName)
              );
            } else if (event.type === 'UPDATE' && event.data.analysisName) {
              const analysis = item.group.analyses.find((a) => a.name === event.data.analysisName);
              if (analysis) {
                const collectionItem = this.tableItems.find(
                  (i) => i.type === 'collection' && i.id === event.data.collectionName
                );
                if (collectionItem) {
                  const updatedAnalysis = collectionItem.collection.analyses.find(
                    (a) => a.name === event.data.analysisName
                  );
                  if (updatedAnalysis) {
                    Object.assign(analysis, updatedAnalysis);
                  }
                }
              }
            }
            item.displayData.analysisCount = item.group.analyses.length;
            item.displayData.docCount = ProjectAnalysisGroup.getDocumentCount(item.group);
          });
      });
    };

    this.projectDetailService.analysisEvent
      .pipe(
        filter((event) => event.type === 'CREATE'),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        handleActionOnCollection(event.data.collectionName, (collectionItem) => {
          // This is to prevent duping analyses and pushing it multiple times
          const analysesIncludesEvent = collectionItem.collection.analyses.some(
            (an) => an.name === event.data.name
          );
          if (analysesIncludesEvent) {
            return;
          }

          collectionItem.collection.analyses.push(event.data);
        });

        handleActionOnAnalysisGroupCollection({
          type: event.type,
          data: {
            collectionName: event.data.collectionName,
          },
        });

        if (
          (this.activeTableTabIndex === 0 || this.activeTableTabIndex === 2) &&
          this.subTableItem?.type === 'collection' &&
          this.subTableItem.id === event.data.collectionName
        ) {
          // Check if item is present in the subTableItems (i don't know if a case exists where it's not already added here)
          // Here we prevent duping analyses
          const idx = this.subTableItem.collection.analyses.findIndex(
            (a) => a.name === event.data.name
          );
          if (idx === -1) {
            this.subTableItem.collection.analyses.push(event.data);
          }

          const newAnalysis = ProjectAnalysis.createAnalysisItem(
            event.data,
            this.subTableItem.collection,
            this.project
          );

          if (newAnalysis) {
            this.projectItemsDatasource.data = [...this.projectItemsDatasource.data, newAnalysis];
            this.sortTable();
          }
        }
      });

    this.projectDetailService.analysisEvent
      .pipe(
        filter((event) => event.type === 'UPDATE'),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        handleActionOnCollection(event.data.collectionName, (collectionItem) => {
          const idx = collectionItem.collection.analyses.findIndex(
            (analysis) => analysis.name === event.data.name
          );
          collectionItem.collection.analyses.splice(idx, 1, event.data);
        });

        // Update analysis groups
        handleActionOnAnalysisGroupCollection({
          type: event.type,
          data: {
            collectionName: event.data.collectionName,
            analysisName: event.data.name,
          },
        });

        if (
          (this.activeTableTabIndex === 0 || this.activeTableTabIndex === 2) &&
          this.subTableItem?.type === 'collection' &&
          this.subTableItem.id === event.data.collectionName
        ) {
          const idx = this.subTableItem.collection.analyses.findIndex(
            (analysis) => analysis.name === event.data.name
          );
          this.subTableItem.collection.analyses[idx] = event.data;

          const tableIdx = this.projectItemsDatasource.data.findIndex(
            (analysis) => analysis.id === event.data.name
          );
          const analysisItem = this.projectItemsDatasource.data[tableIdx];
          if (analysisItem) {
            const previousName = analysisItem.name;
            analysisItem.analysis = event.data;
            if (event.data.displayName !== previousName) {
              const updatedAnalysis = ProjectAnalysis.createAnalysisItem(
                event.data,
                this.subTableItem.collection,
                this.project
              );
              if (updatedAnalysis) {
                this.projectItemsDatasource.data[tableIdx] = updatedAnalysis;
                this.projectItemsDatasource.data = this.projectItemsDatasource.data;
              }
            }
          }
        }
      });

    this.projectDetailService.analysisEvent
      .pipe(
        filter((event) => event.type === 'DELETE'),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        handleActionOnCollection(
          event.data.collectionName,
          (collectionItem) =>
            (collectionItem.collection.analyses = collectionItem.collection.analyses.filter(
              (analysis) => analysis.name !== event.data.name
            ))
        );

        handleActionOnAnalysisGroupCollection({
          type: event.type,
          data: {
            collectionName: event.data.collectionName,
            analysisName: event.data.name,
          },
        });

        if (
          (this.activeTableTabIndex === 0 || this.activeTableTabIndex === 2) &&
          this.subTableItem?.type === 'collection' &&
          this.subTableItem.id === event.data.collectionName
        ) {
          this.subTableItem.collection.analyses = this.subTableItem.collection.analyses.filter(
            (analysis) => analysis.name !== event.data.name
          );

          this.projectItemsDatasource.data = this.projectItemsDatasource.data.filter(
            (item) => item.id !== event.data.name
          );
        }
      });

    this.projectDetailService.analysisGroupCollectionEvent
      .pipe(takeUntil(this.unsub$))
      .subscribe(handleActionOnAnalysisGroupCollection);

    this.projectDetailService.collectionEvent
      .pipe(
        filter(() => this.activeTableTabIndex === 0 || this.activeTableTabIndex === 2),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        const tableCollIdx = this.projectItemsDatasource.data.findIndex(
          (item) => item.type === 'collection' && item.id === event.data.collectionName
        );
        if (event.type === 'DELETE') {
          this.projectItemsDatasource.data.splice(tableCollIdx, 1);
          this.projectItemsDatasource.data = [...this.projectItemsDatasource.data];
        }
        if (event.type === 'UPDATE') {
          const isSubTableItem =
            this.subTableItem?.type === 'collection' &&
            this.subTableItem.id === event.data.collectionName;

          if (tableCollIdx !== -1 || isSubTableItem) {
            const updatedColl = isSubTableItem
              ? this.subTableItem.collection.data
              : this.projectItemsDatasource.data[tableCollIdx]?.collection?.data;
            const oldColl = this.tableItems.find(
              (item) => item.type === 'collection' && item.id === event.data.collectionName
            )?.collection?.data;

            const areCollectionsEqual = Utils.deepEquals(oldColl, event.data, ['indexedAt']);
            const isNewCountEqual = updatedColl?.documentCount === event.data?.documentCount;
            const isUpload =
              event.data.origin?.length === 1 && event.data.origin?.some((o) => o === 'UPLOAD');
            const isSubTableItemRenamed =
              isSubTableItem && this.subTableItem.name !== event.data.displayName;

            const projectCollection = ProjectCollection.createProjectCollectionFromProject(
              this.project,
              event.data
            );

            const projCollItem = ProjectCollection.createCollectionItem(
              projectCollection,
              this.configurations,
              this.account
            );

            if (
              this.getIndexState(projCollItem) !== 'STARTED' &&
              this.getCollectionAnalyzeState(projCollItem) !== 'STARTED' &&
              (!areCollectionsEqual || isSubTableItemRenamed)
            ) {
              /*
              Re-initialize data to update table row display
              When docs are newly added to the collection;
            */
              this.projectItemsDatasource.data[tableCollIdx] = projCollItem;
              this.projectItemsDatasource.data = this.projectItemsDatasource.data;

              /*
              Wait for collections update event to finish
              Before calling a re-load Project Items Data:
              a. origin is UPLOAD
              b. origin is COPY and NEW Collection
              c. origin is COPY and Collection is EXISTING -- First Time Copied
            */
              if (!isNewCountEqual || isUpload) {
                this.setProjectItemData();
              }
            }
          }
        }
      });
  }

  private subscribeToDriverAnalysisEvents() {
    this.projectDetailService.driverAnalysisEvent
      .pipe(
        filter((event) => event.type === 'UPDATE'),
        filter(
          (event) =>
            this.subTableItem?.type === 'collection' &&
            this.subTableItem.id === event.data.collectionName
        ),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        const idx = this.subTableItem.collection.driverAnalyses.findIndex(
          (analysis) => analysis.id === event.data.calculationId
        );
        if (idx > -1) {
          this.subTableItem.collection.driverAnalyses[idx].status = event.data.status;
        }

        const tableIdx = this.projectItemsDatasource.data.findIndex(
          (analysis) => analysis.id === event.data.calculationId
        );
        this.projectItemsDatasource.data[tableIdx].calculation.status = event.data.status;
      });

    this.projectDetailService.driverAnalysisEvent
      .pipe(
        filter((event) => event.type === 'DELETE'),
        filter(
          (event) =>
            this.subTableItem?.type === 'collection' &&
            this.subTableItem.id === event.data.collectionName
        ),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        this.subTableItem.collection.driverAnalyses =
          this.subTableItem.collection.driverAnalyses.filter(
            (analysis) => analysis.id !== event.data.calculationId
          );

        this.projectItemsDatasource.data = this.projectItemsDatasource.data.filter(
          (item) => item.id !== event.data.calculationId
        );
      });
  }

  private subscribeToAnalysisGroupEvents() {
    this.projectDetailService.analysisGroupEvent
      .pipe(
        filter(() => this.activeTableTabIndex === 0 || this.activeTableTabIndex === 3),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => {
        const tableIdx = this.projectItemsDatasource.data.findIndex(
          (group) => group.id === event.data.id
        );
        const tableItemIdx = this.tableItems.findIndex((grp) => grp.id === event.data.id);
        const objectLabels = (
          this.projectItemsDatasource.data[tableIdx] ?? this.tableItems[tableItemIdx]
        )?.labels as ObjectLabel[];
        if (objectLabels?.length) {
          event.data.objectLabels = objectLabels;
        }
        const group = ProjectAnalysisGroup.createGroupItem(event.data, this.project);

        // Upon creation we add the new element
        if (event.type === 'CREATE') {
          this.tableItems.push(group);
          this.projectItemsDatasource.data.unshift(group);
        }

        // Upon Delete we must update the two main resources where we store items to pouplate the tables
        if (event.type === 'DELETE') {
          this.tableItems.splice(tableItemIdx, 1);
          this.projectItemsDatasource.data.splice(tableIdx, 1);
          this.projectItemsDatasource.data = [...this.projectItemsDatasource.data];
        }

        // UPDATE
        // When we are observing the AG details we need to update the tables accordingly
        if (event.type === 'UPDATE') {
          if (this.subTableItem) {
            const isRename = this.subTableItem.name !== group.name;
            const groupAnalyses = group.group.analyses
              .map((analysis) => {
                const projectCollection = this.projectCollections.find(
                  (pColl) => pColl.data.collectionName === analysis.collectionName
                );

                return ProjectAnalysis.createAnalysisItem(
                  analysis,
                  projectCollection,
                  this.project
                );
              })
              .filter((a) => !!a);
            if (groupAnalyses.length) {
              this.projectItemsDatasource.data = groupAnalyses;
            }
            if (isRename) {
              this.tableItems[tableItemIdx] = group;
            }
          } else {
            // Regular update handling
            this.tableItems[tableItemIdx] = group;
            this.projectItemsDatasource.data[tableIdx] = group;
          }
          // Trigger change detection b/c reasons
          this.projectItemsDatasource.data = [...this.projectItemsDatasource.data];
        }
        if (event.type !== 'DELETE') {
          // update docs count
          this.calculateFilteredDocuments(group);
        }
        // create projection breadcrumbs
        if (this.subTableItem) {
          this.prepareProjection(
            this.subTableItem.type ?? 'group',
            this.subTableItem.id,
            tableIdx,
            group
          );
        }
      });
  }

  private setPropTranslations(t: { [key: string]: string }) {
    this.createItemDropdown[0].title = t['dashboard'];
    this.createItemDropdown[1].title = t['collection'];
    this.createItemDropdown[2].title = t['analysis-group'];

    this.vibeItUpDropdown[0].title = t['vibe-it-up-generate-dashboard'];
    this.vibeItUpDropdown[1].title = t['vibe-it-up-chat-with-data'];

    this.tableTabs = [
      {
        id: 'all',
        label: t['all'],
        projectionLabel: t['all'],
        buttonLabel: t['create'],
        icon: undefined,
        count: undefined,
      },
      {
        id: 'dashboard',
        label: t['dashboards'],
        projectionLabel: t['all-dashboards'],
        buttonLabel: t['create-dashboard'],
        emptyText: t['no-dashboards-found'],
        icon: undefined,
        count: undefined,
      },
      {
        id: 'collection',
        label: t['collections'],
        projectionLabel: t['all-collections'],
        buttonLabel: t['create-collection'],
        emptyText: t['no-collections-found'],
        icon: undefined,
        count: undefined,
      },
      {
        id: 'group',
        label: t['analysis-groups'],
        projectionLabel: t['all-analysis-groups'],
        buttonLabel: t['create-analysis-group'],
        emptyText: t['no-analysis-groups-found'],
        icon: undefined,
        count: undefined,
      },
    ];

    this.importValidator = (fileUpload, importedItem) => {
      if (!importedItem.dashboard_version) {
        return {
          isValid: false,
          message: `${t['invalid-import']} ${fileUpload.file.name}`,
        };
      }

      return {
        isValid: true,
      };
    };
  }

  private async init() {
    this.isLoading = true;
    const [account, configRes, embeddingsEnabled] = await Promise.all([
      this.accountService.sub.latest(),
      this.projectDetailService.loadConfigurations(),
      this.accountService.hasAccountFeature('INTERACTIVE_SMART_SUMMARIES'),
    ]);
    this.account = account;
    this.groupConfigurations = configRes.groupConfigurations;
    this.configurations = configRes.configurations;
    this.embeddingsEnabled = embeddingsEnabled;
    this.isLoading = false;
  }

  private calculateFilteredDocuments(group: ProjectItem) {
    if (!group.displayData.docCount) {
      this.analysisService
        .getAnalysisDocumentCount(this.project.id, group.group.lookupId)
        .then((res) => {
          const analysisIds = group.group.analyses.map((a) => a.analysisName ?? a.name);
          const groupAnalysisCounts = res.documentCounts.filter((bucket) =>
            analysisIds.includes(bucket.value)
          );
          groupAnalysisCounts.forEach(
            (bucket) =>
              (group.group.analyses.find((a) => a.analysisName === bucket.value).documentCount =
                bucket.count)
          );
          group.displayData.docCount = groupAnalysisCounts.reduce(
            (prev, curr) => prev + curr.count,
            0
          );
        });
    }
  }

  private setProjectItemData() {
    this.tableItems = [];
    const urlTabName = this.route.snapshot.params['tab'] as string | undefined;
    const urlTabIndex =
      urlTabName != null
        ? ((TableType[urlTabName as keyof typeof TableType] as number | undefined) ?? -1)
        : -1;
    this.activeTableTabIndex =
      urlTabIndex !== -1 ? urlTabIndex : (this.projectDetailService.getLastSelectedTab() ?? 0);
    this.tableTabs.forEach((t) => (t.count = 0));

    if (this.isExecutive) {
      this.tableTabs = this.tableTabs.filter((t) => t.id === 'dashboard');
      this.activeTableTabIndex = 0;
      for (const dashboard of this.project.dashboards) {
        if (dashboard.payload.isExecutive) {
          this.tableItems.push(ProjectDashboard.createDashboardItem(dashboard, this.project));
          this.tableTabs[0].count++;
        }
      }
    } else {
      this.projectCollections = ProjectCollection.createListFromProject(this.project);

      for (const collection of this.projectCollections) {
        this.tableItems.push(
          ProjectCollection.createCollectionItem(collection, this.configurations, this.account)
        );

        this.tableTabs[2].count++;
      }
      for (const group of this.project.groups) {
        const projectGroup = ProjectAnalysisGroup.createGroupItem(group, this.project);
        this.tableItems.push(projectGroup);
        this.tableTabs[3].count++;

        if (group.lookupId) {
          this.calculateFilteredDocuments(projectGroup);
        }
      }
      for (const dashboard of this.project.dashboards) {
        this.tableItems.push(ProjectDashboard.createDashboardItem(dashboard, this.project));
        this.tableTabs[1].count++;
      }
      this.tableTabs[0].count = this.tableItems.length;

      this.createItemDropdown[0].disabled = !this.dashboardActions.canShowNewDashboardDialog;
      this.createItemDropdown[0].subtitle = this.createItemDropdown[0].disabled
        ? translate('project.project-details-table.create-collection-with-analysis')
        : undefined;
      this.createItemDropdown[2].disabled = !this.groupActions.hasAnalyses;
      this.createItemDropdown[2].subtitle = this.createItemDropdown[2].disabled
        ? translate('project.project-details-table.create-collection-with-analysis')
        : undefined;

      this.vibeItUpDisabled = !this.dashboardActions.canShowNewDashboardDialog;
      this.vibeItUpSubtitle = this.vibeItUpDisabled
        ? translate('project.project-details-table.create-collection-with-analysis')
        : undefined;
      this.createItemDropdown = [...this.createItemDropdown];
    }

    this.projectItemsDatasource = new MatTableDataSource(this.tableItems);
    this.projectItemsDatasource.filterPredicate = (data, filterStr: string) => {
      if (!this.projectionBreadcrumbs) {
        return data.name.toLowerCase().includes(filterStr.toLowerCase());
      }
      return true;
    };

    if (this.filterValue) {
      this.projectItemsDatasource.filter = this.filterValue;
    }

    this.onTabClicked(this.tableTabs[this.activeTableTabIndex].id);

    const urlParentItemId = this.route.snapshot.params['parentItem'] as string | undefined;
    const subTableItemId = urlParentItemId ?? this.projectDetailService.getLastSelectedSubItemId();
    this.subTableItem = this.tableItems.find((c) => c.id === subTableItemId);
    if (this.subTableItem) {
      this.projectDetailService.changeCurrentSubTableItem(this.subTableItem);
    }
  }

  async onTabChanged(tabId, itemId?) {
    const newTabIndex = this.tableTabs.findIndex((t) => t.id === tabId);
    if (newTabIndex !== this.activeTableTabIndex) {
      this.onFilter('');
    }
    this.activeTableTabIndex = newTabIndex;
    this.projectDetailService.storeLastSelectedTab(this.activeTableTabIndex);

    this.projectionBreadcrumbs = null;
    this.projectItemsColumns = ['type', 'name', 'details', 'modified', 'actions'];
    this.labelsColumnSpan = 3;

    this.projectItemsDatasource.data = this.tableItems;
    if (this.activeTableTabIndex !== 0) {
      this.projectItemsDatasource.data = this.tableItems.filter((item) => item.type === tabId);
    }

    if (itemId) {
      let itemToSelectIdx = this.projectItemsDatasource.data.findIndex(
        (item) => item.id === itemId
      );
      let itemToSelect = this.projectItemsDatasource.data[itemToSelectIdx];

      if (this.selectedItemProjectionId) {
        const projectedItemData = await this.prepareProjection(
          tabId,
          itemId,
          itemToSelectIdx,
          itemToSelect
        );
        itemToSelectIdx = projectedItemData.itemToSelectIdx;
        itemToSelect = projectedItemData.itemToSelect;

        this.onTableSortChange(this.sort);
      }

      this.itemSelected(itemToSelect, itemToSelectIdx);
      return;
    }

    if (
      this.selectedItem &&
      !this.projectItemsDatasource.data.some((item) => item.id === this.selectedItem.id)
    ) {
      this.projectDetailService.removeLastSelectedItem();
    }

    this.autoSelectTableItem();
  }

  async makePrimaryAnalysis(analysis: Analysis) {
    const { notes, displayName, collectionName, name } = analysis;

    const updated = {
      projectId: this.project.id,
      collectionName,
      notes,
      displayName,
      analysisId: name,
      makePrimary: true,
    };

    try {
      const res = await this.analysisService.updateAnalysis(updated);

      if (res) {
        this.projectItemsDatasource.data = this.projectItemsDatasource.data.map((data) => {
          if (data.type === 'analysis') {
            if (!data.analysis) {
              return data;
            }
            if (data.analysis.name === res.name) {
              return {
                ...data,
                analysis: {
                  ...data.analysis,
                  primary: res.primary,
                },
              };
            }
          }

          return { ...data, analysis: { ...data.analysis, primary: false } };
        });

        this.tableItems = this.tableItems.map((item) => {
          if (item.type === 'collection' && item.id === res.collectionName) {
            item.collection.analyses = item.collection.analyses.map((a: Analysis) => ({
              ...a,
              primary: a.name === res.name,
            }));
          }

          if (item.type === 'group') {
            item.group.analyses = item.group.analyses.map((a: Analysis) => {
              if (a.collectionName === res.collectionName) {
                // Again duality of keynames when it comes to analysis inside a group
                return {
                  ...a,
                  primary: a.analysisName === res.name || a.name === res.name,
                };
              }

              return a;
            });
          }

          return item;
        });
        // Mirror changes to the whole project object since we'll create and assign projections from there in setProjectItemData
        this.project.analyses = this.project.analyses.map((item) => ({
          ...item,
          primary: item.name === analysis.name,
        }));
      }
    } catch (error) {
      this.logger.error('makePrimaryAnalysis failed', { error });
    }
  }

  private async prepareProjection(tabId, itemId, itemToSelectIdx, itemToSelect: ProjectItem) {
    this.subTableItem = itemToSelect;
    this.projectDetailService.storeLastSelectedSubItemId(this.subTableItem.id);
    this.projectionBreadcrumbs = [
      {
        label: this.tableTabs[this.activeTableTabIndex].projectionLabel,
        action: () => {
          this.selectedItemProjectionId = null;
          this.subTableItem = null;
          this.projectDetailService.removeLastSelectedSubItemId();
          this.onTabChanged(tabId, itemId);
          this.router.navigate(['.', { tab: tabId }], { relativeTo: this.route, replaceUrl: true });
        },
      },
      {
        label: itemToSelect.name,
      },
    ];
    this.projectItemsColumns = ['type', 'name', 'config', 'details', 'modified', 'actions'];
    this.labelsColumnSpan = 4;

    if (itemToSelect.group) {
      this.projectItemsColumns.splice(3, 0, 'collection');
      this.labelsColumnSpan = 5;
    }

    const analyses = itemToSelect.collection?.analyses ?? itemToSelect.group?.analyses;
    const projectAnalyses = analyses
      .map((item) => {
        const projectCollection = this.projectCollections.find(
          (c) => c.data.collectionName === item.collectionName
        );
        return ProjectAnalysis.createAnalysisItem(item, projectCollection, this.project);
      })
      .filter((a) => !!a);

    if (itemToSelect.displayData.isDriverAnalysis) {
      this.isLoading = true;

      const projectCollection = this.projectCollections.find(
        (c) => c.data.collectionName === analyses[0]?.collectionName
      );

      let driverAnalyses = itemToSelect.collection.driverAnalyses;
      if (!driverAnalyses.length) {
        driverAnalyses = (
          await this.predictiveService.listCalculations(this.project.id, itemToSelect.id, {
            pageSize: 1024,
            sideloads: 'objectLabels',
          })
        ).items;
      }

      projectAnalyses.push(
        ...driverAnalyses.map((kdaItem) =>
          ProjectAnalysis.createKdaAnalysisItem(
            analyses[0],
            kdaItem,
            projectCollection,
            this.project
          )
        )
      );
    }

    this.projectItemsDatasource.data = projectAnalyses;
    const projectedItemToSelectIdx = this.projectItemsDatasource.data.findIndex(
      (item) => item.id === this.selectedItemProjectionId
    );
    const projectedItemToSelect = this.projectItemsDatasource.data[projectedItemToSelectIdx];
    this.isLoading = false;

    return {
      itemToSelectIdx: projectedItemToSelectIdx,
      itemToSelect: projectedItemToSelect,
    };
  }

  onTabClicked(tabId, $event?) {
    this.projectDetailService.changeCurrentTab(tabId);
    if ($event) {
      this.subTableItem = null;
      this.projectDetailService.removeLastSelectedSubItemId();
      this.router.navigate(['.', { tab: tabId }], { relativeTo: this.route });
    }
  }

  private syncStateFromRoute(params: Params): void {
    const tabName = params['tab'] as string | undefined;
    const parentItemId = params['parentItem'] as string | undefined;
    if (parentItemId) {
      this.selectedItemProjectionId = parentItemId;
      this.onTabChanged(tabName, parentItemId);
      return;
    }

    if (!parentItemId && this.subTableItem) {
      this.selectedItemProjectionId = null;
      this.subTableItem = null;
      this.projectDetailService.removeLastSelectedSubItemId();
      this.onTabChanged(tabName ?? this.tableTabs[this.activeTableTabIndex]?.id);
      return;
    }

    const tabIndex = TableType[tabName as keyof typeof TableType] as number | undefined;
    if (tabIndex != null && tabIndex !== this.activeTableTabIndex) {
      this.onTabChanged(tabName);
    }
  }

  onCreateItem(id: string) {
    switch (id) {
      case 'dashboard':
        this.dashboardActions.onShowNewDashboardDialog(this.sort);
        break;
      case 'collection':
        this.collectionActions.onShowNewCollectionDialog();
        break;
      case 'group':
        this.groupActions.onShowNewAnalysisGroupDialog();
        break;
    }
  }

  public onVibeItUpDropdownSelect(optionId: string): void {
    if (this.vibeItUpDisabled) {
      return;
    }
    if (optionId === 'dashboard' || optionId === 'chat') {
      this.openSpotlightIdeasDialog(optionId);
    }
  }

  private openSpotlightIdeasDialog(mode: 'dashboard' | 'chat'): void {
    if (!this.projectCollections?.length) {
      return;
    }

    const sorted = [...this.projectCollections].sort((a, b) => {
      const aDate = a.data.indexedAt ? new Date(a.data.indexedAt).getTime() : 0;
      const bDate = b.data.indexedAt ? new Date(b.data.indexedAt).getTime() : 0;
      return bDate - aDate;
    });

    const collections = sorted
      .filter(
        (c) =>
          c.data?.collectionName &&
          (c.data?.documentCount ?? 0) > 0 &&
          (c.data?.analysisCount ?? 0) > 0
      )
      .map((c) => ({
        id: c.data.collectionName,
        name: c.data.displayName || c.data.collectionName,
        contentChannel: c.data.contentChannel,
        hasSections: c.data.hasSections,
      }));

    if (!collections.length) {
      this.alertService.showToast(
        translate('project.project-details-table.create-collection-with-analysis'),
        5000,
        'warning'
      );
      return;
    }

    this.spotlightIdeasDialogMode = mode;
    this.spotlightIdeasDialogData = {
      projectId: this.project.id,
      collections,
    };
    this.showSpotlightIdeasDialog = true;
  }

  public onSpotlightIdeasDialogClosed(result: SpotlightDashboardIdeasDialogResult | null): void {
    this.showSpotlightIdeasDialog = false;
    if (!result?.minimized || !result.generationPromise) {
      return;
    }

    const defaultName = translate(
      'project.project-details-table.vibe-it-up-default-dashboard-name'
    );
    const generatingName = result.selectedIdeaTitle || defaultName;

    this.alertService.showToast(
      translate('project.project-details-table.vibe-it-up-generating-toast', {
        name: generatingName,
      }),
      3000
    );

    result.generationPromise.then((genResult) => {
      if (!genResult.redirectUrl) {
        return;
      }

      const name = genResult.dashboardName || generatingName;
      const toast = translate('project.project-details-table.vibe-it-up-ready-toast', {
        name,
        url: genResult.redirectUrl,
      });
      this.alertService.showToast(toast, 0, 'default', false, 'snackbar-link');
    });
  }

  onOpenItem(item: ProjectItem, $event) {
    if (item.isSocial) {
      return;
    }

    switch (item.type) {
      case 'dashboard':
        this.dashboardActions.gotoDashboard(item.dashboard, $event);
        break;
      case 'collection': {
        if (this.canOpenCollection(item.collection)) {
          this.selectedItemProjectionId = item.collection.data.collectionName;
        }
        const collectionTabId = this.tableTabs[this.activeTableTabIndex].id;
        this.onTabChanged(collectionTabId, item.collection.data.collectionName);
        if ($event !== null) {
          this.router.navigate(
            [
              '.',
              {
                tab: TableType[this.activeTableTabIndex],
                parentItem: item.collection.data.collectionName,
              },
            ],
            { relativeTo: this.route }
          );
        }
        break;
      }
      case 'group': {
        if (!this.isReadOnly) {
          this.selectedItemProjectionId = item.group.id;
          const groupTabId = this.tableTabs[this.activeTableTabIndex].id;
          this.onTabChanged(groupTabId, item.group.id);
          if ($event !== null) {
            this.router.navigate(
              ['.', { tab: TableType[this.activeTableTabIndex], parentItem: item.group.id }],
              { relativeTo: this.route }
            );
          }
        }
        break;
      }
    }
  }

  itemSelected(item: ProjectItem, index: number, scroll = true) {
    if (item === this.selectedItem) {
      return;
    }
    this.projectDetailService.changeSelectedItem(item);
    if (index !== -1) {
      if (!this.selectedItemProjectionId) {
        const lastSelectedItem = {
          itemId: item.id,
          projectId: this.project.id,
          index,
          sortDir: this.sort.direction,
          sortProp: this.sort.active,
        };

        this.projectDetailService.storeLastSelectedItem(lastSelectedItem);
      }

      if (scroll) {
        this.scrollToItemId(item.id);
      }
    }
  }

  private autoSelectTableItem() {
    const lastSelectedItem = this.projectDetailService.getLastSelectedItem();
    if (lastSelectedItem) {
      this.sort.direction = lastSelectedItem.sortDir ?? 'desc';
      this.sort.active = lastSelectedItem.sortProp ?? 'modified';
    }
    if (this.route.snapshot.params['tab'] == null) {
      this.activeTableTabIndex = this.projectDetailService.getLastSelectedTab() ?? 0;
    }

    this.onTableSortChange(this.sort);
    if (lastSelectedItem && lastSelectedItem.projectId === this.project.id) {
      const itemIdx = this.projectItemsDatasource.data.findIndex(
        (i) => i.id === lastSelectedItem.itemId
      );
      //Select the last visited item, if present
      if (itemIdx !== -1) {
        const item = this.projectItemsDatasource.data[itemIdx];
        this.itemSelected(item, itemIdx);
        return;
      }

      //If item has been deleted, go to next or previous item
      if (this.projectItemsDatasource.data.length > 0) {
        const prevIdx = lastSelectedItem.index - 1;
        const prevItem = this.projectItemsDatasource.data[prevIdx];
        const nextIdx = lastSelectedItem.index;
        const nextItem = this.projectItemsDatasource.data[nextIdx];

        if (nextItem) {
          this.itemSelected(nextItem, nextIdx);
          return;
        }

        if (prevItem) {
          this.itemSelected(prevItem, prevIdx);
          return;
        }
      }
    } else {
      if (this.projectItemsDatasource.data.length > 0) {
        this.itemSelected(this.projectItemsDatasource.data[0], 0);
      }
    }
    if (this.projectItemsDatasource.data.length === 0) {
      this.itemSelected(null, -1);
    }
  }

  private scrollToItemId(id: string): void {
    setTimeout(() => {
      const elem = this.rows.find((row) => {
        return row.nativeElement.id === id;
      });

      elem?.nativeElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 1000);
  }

  onFilter(val: string) {
    const filterVal = val ? val.trim() : '';
    this.search$.next(filterVal);
  }

  onTableSortChange($event = this.sort) {
    this.sort = $event;

    this.sortTable($event);

    if (this.selectedItem) {
      this.scrollToItemId(this.selectedItem.id);
    }

    const lastSelectedItem = this.projectDetailService.getLastSelectedItem();
    if (lastSelectedItem) {
      lastSelectedItem.sortDir = this.sort.direction;
      lastSelectedItem.sortProp = this.sort.active;

      this.projectDetailService.storeLastSelectedItem(lastSelectedItem);
    }
  }

  private sortTable(sort = this.sort) {
    const direction = sort.direction === 'asc' ? 1 : -1;

    this.projectItemsDatasource.data.sort((i1: ProjectItem, i2: ProjectItem) => {
      const ret = this.sortTypeOrder[i1.type] - this.sortTypeOrder[i2.type];

      switch (sort.active) {
        case 'name':
          return ret || i1.name.localeCompare(i2.name) * direction;
        case 'modified':
          return ret || (i1.modified.getTime() - i2.modified.getTime()) * direction;
      }
    });

    this.projectItemsDatasource.data = [...this.projectItemsDatasource.data];
  }

  canOpenCollection(collection: ProjectCollection) {
    return collection;
  }

  hideKabobMenu(item: ProjectItem) {
    return (
      this.isReadOnly &&
      (item.type === 'group' ||
        (item.type === 'collection' &&
          !item.collection.analyses?.length &&
          !item.collection.uploads?.length) ||
        (item.type === 'analysis' && item.calculation))
    );
  }

  onCloseImportDashboardDialog($event) {
    this.showImportDialog = false;
    if ($event) {
      this.dashboardActions.onShowNewDashboardDialog(this.sort, $event.dashboard);
    }
  }

  onDeleteKeyDriverAnalysis(item: ProjectItem) {
    const collection = this.projectCollections.find(
      (pc) => pc.data.collectionName === item.calculation.collectionId
    );
    this.collectionActions.showDeleteDriverAnalysisDialog(
      collection,
      collection.data.collectionName,
      this.project.id,
      item.calculation.id,
      item.calculation.displayName
    );
  }

  getCollectionAnalyzeState(item: ProjectItem): string {
    return this.getAnalyzeState(item.collection?.activeAnalysis);
  }

  getIndexState(item: ProjectItem): UploadState {
    const state = item.collection?.indexState ?? null;
    if (state === UploadState.COMPLETE) {
      return null;
    }
    return state;
  }

  getAnalyzeState(analysis: Analysis): string {
    switch (analysis?.status) {
      case 'OPEN':
      case 'QUEUED':
        return 'STARTED';
      case 'DELETED':
      case 'DELETING':
        return 'DELETED';
      case 'ERROR':
        return 'ERROR';
      default:
        return null;
    }
  }

  isItemAnalyzing(item: ProjectItem) {
    if (item.type !== 'analysis') {
      return false;
    }

    if (item.analysis) {
      return this.getAnalyzeState(item.analysis) === 'STARTED';
    }

    if (item.calculation) {
      return item.calculation.status === 'PENDING' || item.calculation.status === 'RUNNING';
    }
  }

  isItemDeleting(item: ProjectItem) {
    if (item.type !== 'analysis') {
      return false;
    }

    if (item.analysis) {
      return this.getAnalyzeState(item.analysis) === 'DELETED';
    }

    if (item.calculation) {
      return item.calculation.status === 'DELETING';
    }
  }

  showDeleteAnalysis(item: ProjectItem): boolean {
    return (
      !item.displayData.isDriverAnalysis &&
      item.analysis.status !== 'OPEN' &&
      item.analysis.status !== 'QUEUED' &&
      item.analysis.status !== 'DELETING' &&
      item.analysis.status !== 'DELETED' &&
      !this.isReadOnly
    );
  }

  get reAnalyzeDisabled() {
    return (
      this.subTableItem.collection.data.locked ||
      this.subTableItem.collection.data.isAutoAnalyze ||
      !!this.subTableItem.collection.activeAnalysis
    );
  }

  createEmbeddingsDisabled(item: ProjectItem) {
    return (
      this.reAnalyzeDisabled ||
      !this.isAdmin ||
      !this.embeddingsEnabled ||
      (item.analysis.embeddingStatus &&
        !['NONE', 'OUTDATED', 'FINISHED', 'FINAL_ERROR'].includes(item.analysis.embeddingStatus))
    );
  }

  showCreateEmbeddings(item: ProjectItem) {
    return this.showDeleteAnalysis(item) && this.isDebug;
  }

  showEmbeddingStatus(item: ProjectItem): boolean {
    return !item.displayData.isDriverAnalysis && !this.isReadOnly;
  }

  showRemoveEmbeddings(item: ProjectItem): boolean {
    return (
      !item.displayData.isDriverAnalysis &&
      item.analysis.embeddingStatus &&
      (item.analysis.embeddingStatus === 'FINAL_ERROR' ||
        item.analysis.embeddingStatus === 'TOO_MANY_CONTINUES' ||
        item.analysis.embeddingStatus === 'FINISHED') &&
      this.isDebug
    );
  }

  disableUploadDocuments(data: Collection) {
    return data.locked || data.origin?.some((type) => type !== OriginTypes.UPLOAD);
  }

  onAutoAnalyzeToggle(event: any, subTableItem: ProjectItem) {
    subTableItem.collection.data.isAutoAnalyze = event.detail.isToggled;
    this.collectionActions.onAutoAnalysisButtonClick(subTableItem);
  }
}
